import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

/**
 * Centralized logout hook that handles all logout scenarios
 * Provides consistent behavior across the application
 */
export const useLogout = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  /**
   * Perform logout with proper flow control
   * @param options - Logout configuration options
   * @returns Promise<boolean> - Success status
   */
  const performLogout = useCallback(async (options: {
    showSuccessMessage?: boolean;
    redirectTo?: string;
    reason?: string;
  } = {}): Promise<boolean> => {
    const {
      showSuccessMessage = true,
      redirectTo = '/login',
      reason
    } = options;

    // Performance monitoring
    const startTime = performance.now();

    try {
      // Show single, fast logout message
      const toastId = toast.info('Logging out...', {
        description: reason || 'Please wait while we secure your session',
        duration: 2000, // Short duration for speed
      });

      // Perform logout with timeout for performance
      const logoutPromise = logout();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Logout timeout')), 3000)
      );
      
      // Race between logout and timeout
      const success = await Promise.race([logoutPromise, timeoutPromise]) as boolean;
      
      // Dismiss the logout message quickly
      toast.dismiss(toastId);
      
      // Show minimal success feedback only if requested
      if (showSuccessMessage && success) {
        toast.success('Logged out successfully', { duration: 1500 });
      }

      // Navigate immediately for better UX
      navigate(redirectTo, { replace: true });
      
      // Log performance metrics
      const endTime = performance.now();
      console.log(`Logout completed in ${(endTime - startTime).toFixed(2)}ms`);
      
      return success;
    } catch (error) {
      // Handle timeout or other errors gracefully
      if (error instanceof Error && error.message === 'Logout timeout') {
        console.log('Logout timed out, proceeding with local cleanup');
      } else {
        console.error('Logout process failed:', error);
      }
      
      // Navigate immediately regardless of error
      navigate(redirectTo, { replace: true });
      return false;
    }
  }, [logout, navigate]);

  /**
   * User-initiated logout (with confirmation dialog)
   */
  const userLogout = useCallback(async () => {
    return performLogout({
      showSuccessMessage: true,
      redirectTo: '/login'
    });
  }, [performLogout]);

  /**
   * Auto-logout for session timeout (no confirmation needed)
   */
  const autoLogout = useCallback(async (reason: string = 'Session expired') => {
    return performLogout({
      showSuccessMessage: false,
      redirectTo: '/login',
      reason
    });
  }, [performLogout]);

  /**
   * Force logout for security reasons
   */
  const forceLogout = useCallback(async (reason: string = 'Security requirement') => {
    return performLogout({
      showSuccessMessage: false,
      redirectTo: '/login',
      reason
    });
  }, [performLogout]);

  return {
    performLogout,
    userLogout,
    autoLogout,
    forceLogout
  };
};
